<script setup lang="ts">
  import { IconImage, Tooltip } from '@/shared/ui';
  import type { MenuItem } from '@/shared/lib';
  import { checkPermissions } from '@/shared/lib';
  import type { CaslAction, CaslSubject } from '@/shared/lib/casl';
  import { computed } from 'vue';
  import { useRouter } from 'vue-router';

  const props = withDefaults(
    defineProps<{
      isCollapsed: boolean;
      menuItems: MenuItem[];
      menuLabel?: string;
      canCheck?: (action: CaslAction, subject: CaslSubject) => boolean;
    }>(),
    {
      menuLabel: 'MENU',
      canCheck: undefined,
    }
  );

  const router = useRouter();
  const route = computed(() => router.currentRoute.value);

  const filteredMenuItems = computed(() =>
    props.menuItems.filter(
      (item) =>
        !item.permissions ||
        !props.canCheck ||
        checkPermissions(item.permissions, (perm) => props.canCheck!(perm.action, perm.subject), item.matchMode),
    ),
  );

  const isMenuItemActive = (item: MenuItem): boolean => {
    // If routeName is defined, use route.matched for parent-child route detection
    if (item.routeName) {
      return route.value.matched.some((r) => r.name === item.routeName);
    }
    // Fallback to old behavior for items without routeName
    return (
      (item.route && route.value.path === item.route) ||
      item.relatedRoutes?.includes(route.value.path) ||
      false
    );
  };

  const handleMenuItemClass = (item: MenuItem) => {
    return [
      isMenuItemActive(item)
        ? 'menu-item rounded-lg cursor-pointer text-[var(--theme-text)] active'
        : 'menu-item rounded-lg cursor-pointer text-[var(--theme-text)]',
    ];
  };
</script>

<template>
  <nav :class="[isCollapsed ? 'mt-10 px-3' : 'mt-7']">
    <p
      v-if="!isCollapsed"
      class="text-left text-xs text-[var(--theme-secondary-ink)] mb-4"
    >
      {{ menuLabel }}
    </p>
    <p
      v-else
      class="mb-4 text-xs text-center text-[var(--theme-secondary-ink)]"
    >
      {{ menuLabel }}
    </p>
    <ul
      :class="[
        isCollapsed ? 'flex flex-col items-center gap-2' : 'flex flex-col gap-1 mr-1.5 text-center',
      ]"
    >
      <li
        v-for="(item, index) in filteredMenuItems"
        :key="index"
        :class="handleMenuItemClass(item)"
      >
        <Tooltip
          v-if="isCollapsed"
          :content="item.text"
          placement="right"
        >
          <router-link
            :to="item.route || ''"
            :class="[
              isCollapsed
                ? 'flex items-center h-10 p-2 gap-2'
                : 'w-50 h-10 flex py-2 px-3 text-sm font-medium items-center gap-3',
            ]"
          >
            <IconImage
              :src="item.icon"
              :alt="item.alt"
            />
          </router-link>
        </Tooltip>
        <router-link
          v-else
          :to="item.route || ''"
          :class="[
            isCollapsed
              ? 'flex w-10 h-10 py-2 px-2 gap-2'
              : 'w-51 h-10 flex py-2 px-3 text-sm font-medium items-center gap-2',
          ]"
        >
          <IconImage
            :src="item.icon"
            :alt="item.alt"
            class="w-6 h-6 flex-shrink-0"
          />
          <span
            v-if="!isCollapsed"
            class="text-left"
          >{{ item.text }}</span>
        </router-link>
      </li>
    </ul>
  </nav>
</template>

<style scoped>
  .menu-item {
    transition: background-color 0.2s ease-in-out;
  }

  .menu-item:hover {
    background-color: color-mix(in srgb, var(--theme-primary) 90%, var(--theme-secondary));
  }

  .menu-item.active {
    background-color: color-mix(in srgb, var(--theme-primary) 80%, var(--theme-secondary));
  }
</style>
